import React, { useEffect, useState } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import SmallSpinner from "../loading/SmallSpinner";
import { useAppContext } from "../config/AppContext";

// Custom icon creator with gradient and customization
const createGradientIcon = (index, total, customOptions = {}) => {
  // Default options with ability to override
  const defaultOptions = {
    iconSize: [25, 25],
    gradientColors: ["#FF0000", "#0000FF"], // Red to Blue gradient
    borderColor: "black",
    borderWidth: 2,
    opacity: 0.7,
  };

  const options = { ...defaultOptions, ...customOptions };

  // Calculate gradient based on index
  const calculateColor = (index, total, colors) => {
    // Linear interpolation between gradient colors
    const ratio = index / (total - 1);
    const r = Math.round(
      parseInt(colors[0].slice(1, 3), 16) * (1 - ratio) +
        parseInt(colors[1].slice(1, 3), 16) * ratio
    );
    const g = Math.round(
      parseInt(colors[0].slice(3, 5), 16) * (1 - ratio) +
        parseInt(colors[1].slice(3, 5), 16) * ratio
    );
    const b = Math.round(
      parseInt(colors[0].slice(5, 7), 16) * (1 - ratio) +
        parseInt(colors[1].slice(5, 7), 16) * ratio
    );

    return `rgb(${r},${g},${b})`;
  };

  // Create a custom divIcon with gradient
  return L.divIcon({
    className: "custom-div-icon",
    html: `
      <div style="
        width: ${options.iconSize[0]}px; 
        height: ${options.iconSize[1]}px; 
        border-radius: 50%; 
        background: ${calculateColor(index, total, options.gradientColors)};
        border: ${options.borderWidth}px solid ${options.borderColor};
        opacity: ${options.opacity};
      "></div>
    `,
    iconSize: options.iconSize,
    iconAnchor: [options.iconSize[0] / 2, options.iconSize[1] / 2],
  });
};

const AddressMap = ({ items }) => {
  const [addresses, setAddresses] = useState([]);
  const [mapCenter, setMapCenter] = useState([48.8566, 2.3522]); // Default to Paris center
  const [paths, setPaths] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { appState } = useAppContext();
  const prefix = appState.dev ? appState.dev_url : appState.prod_url;

  // Default visual configurations with ability to override
  const defaultVisualConfig = {
    markerGradientColors: ["#51bff9", "#0026ff"], // Green to Red gradient
    markerBorderColor: "white",
    markerBorderWidth: 1,
    markerOpacity: 1,
    markerSize: [15, 15],
    pathColor: "black",
    pathWeight: 1,
    pathOpacity: 1,
    initialZoom: 8,
    tileLayerUrl:
      "https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}{r}.png",
    tileLayerAttribution:
      '&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>',
  };

  async function geocodeAddress(address) {
    try {
      const response = await fetch(
        prefix + `places/geocode?address=${encodeURIComponent(address)}`
      );

      if (!response.ok) {
        console.error("Geocoding error");
        return null;
      }

      const data = await response.json();

      if (data.result) {
        // Assuming the first result contains the location
        // You might need to adjust this based on your exact backend response
        const latitude = data.result.latitude;
        const longitude = data.result.longitude;
        return [latitude, longitude];
      }
      return [latitude, longitude];

      console.warn(`Geocoding failed for address: ${address}`);
      return null;
    } catch (error) {
      console.error("Geocoding error:", error);
      return null;
    }
  }

  const geocodeAndProcessAddresses = async () => {
    try {
      console.log("Items to process:", items);
      if (!items || items.length === 0) return;

      setIsLoading(true);

      // Geocode all input addresses
      const geocodedAddresses = await Promise.all(
        items.map(async (item, index) => {
          const coords = await geocodeAddress(item.address);

          if (!coords) {
            console.error(`Geocoding failed for address: ${item.address}`);
            return null;
          }

          return {
            id: index + 1,
            name: item.name || `Location ${index + 1}`,
            address: item.address,
            coordinates: coords,
          };
        })
      );

      // Filter out any failed geocoding attempts
      const validAddresses = geocodedAddresses.filter((addr) => addr !== null);

      // Set addresses
      setAddresses(validAddresses);

      // Calculate map center
      if (validAddresses.length > 0) {
        const avgLat =
          validAddresses.reduce((sum, addr) => sum + addr.coordinates[0], 0) /
          validAddresses.length;
        const avgLng =
          validAddresses.reduce((sum, addr) => sum + addr.coordinates[1], 0) /
          validAddresses.length;
        setMapCenter([avgLat, avgLng]);
      }

      // Create paths between consecutive addresses
      const generatedPaths = [];
      for (let i = 0; i < validAddresses.length - 1; i++) {
        generatedPaths.push({
          from: validAddresses[i],
          to: validAddresses[i + 1],
          coordinates: [
            validAddresses[i].coordinates,
            validAddresses[i + 1].coordinates,
          ],
        });
      }
      setPaths(generatedPaths);
    } catch (error) {
      console.error("Error processing addresses:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    console.log("Received items:", items);
    if (items && items.length > 0) {
      geocodeAndProcessAddresses();
    }
  }, [items]);

  if (isLoading) {
    return <SmallSpinner></SmallSpinner>;
  }

  if (addresses.length === 0) {
    return <div>Les adresses fournies ne sont pas valides.</div>;
  }

  return (
    <div style={{ height: "100%", width: "100%" }}>
      <MapContainer
        center={mapCenter}
        zoom={defaultVisualConfig.initialZoom}
        style={{ height: "100%", width: "100%" }}
      >
        <TileLayer
          url={defaultVisualConfig.tileLayerUrl}
          attribution={defaultVisualConfig.tileLayerAttribution}
        />

        {/* Render Markers */}
        {addresses.map((address, index) => (
          <Marker
            key={address.id}
            position={address.coordinates}
            icon={createGradientIcon(index, addresses.length, {
              gradientColors: defaultVisualConfig.markerGradientColors,
              borderColor: defaultVisualConfig.markerBorderColor,
              borderWidth: defaultVisualConfig.markerBorderWidth,
              opacity: defaultVisualConfig.markerOpacity,
              iconSize: defaultVisualConfig.markerSize,
            })}
          >
            <Popup>
              <strong>{address.name}</strong>
              <br />
              {address.address}
            </Popup>
          </Marker>
        ))}

        {/* Render Paths */}
        {paths.map((path, index) => (
          <Polyline
            key={index}
            positions={path.coordinates}
            color={defaultVisualConfig.pathColor}
            weight={defaultVisualConfig.pathWeight}
            opacity={defaultVisualConfig.pathOpacity}
          />
        ))}
      </MapContainer>
    </div>
  );
};

export default AddressMap;
